import { useState } from "react";
import { z } from "zod";
import { RichText } from "../RichText";
import { useRenderContext } from "../RenderContext";
import { globalSectionSchema } from "../globalProps";
import { SectionShell } from "../SectionShell";

/* ---------- Schema ---------- */

const fieldItemSchema = z.object({
  label: z.string().default("Question"),
  type: z.enum(["text", "textarea", "select"]).default("text"),
  required: z.boolean().default(false),
  options: z.string().default(""),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Get in Touch"),
  body: z.string().default("Fill out the form below and we will get back to you as soon as possible."),
  submitLabel: z.string().default("Send enquiry"),
  successMessage: z.string().default("Thank you for your enquiry. We will be in touch shortly."),
  showPhone: z.boolean().default(true),
  showMessage: z.boolean().default(true),
  messageLabel: z.string().default("Message"),
  layout: z.enum(["stacked", "two-column"]).default("stacked"),
  fields: z.array(fieldItemSchema).default([]),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const ctx = useRenderContext();
  const isEditor = ctx.isEditor ?? false;

  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  // Standard fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  // Custom field values keyed by index
  const [customValues, setCustomValues] = useState<Record<number, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditor || !ctx.submitLeadForm) return;
    if (formState === "submitting") return;

    setFormState("submitting");
    try {
      const customFields = safe.fields
        .map((field, i) => ({
          label: field.label,
          value: customValues[i] || "",
        }))
        .filter((f) => f.value.trim() !== "");

      const result = await ctx.submitLeadForm({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        message: message.trim() || undefined,
        customFields: customFields.length > 0 ? customFields : undefined,
        honeypot: honeypot || undefined,
      });

      if (result.success) {
        setFormState("success");
        // Reset form
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setHoneypot("");
        setCustomValues({});
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  const inputClass =
    "w-full bg-transparent border border-current/20 rounded-lg px-4 py-3 text-current placeholder-current/50 focus:outline-none focus:border-primary/60 transition-colors text-base";
  const labelClass = "block text-sm font-medium mb-1.5";

  const isTwoColumn = safe.layout === "two-column";

  return (
    <SectionShell globals={safe} widthScale="narrow">
      <div className="text-center mb-10">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold mb-4"
          data-prop="heading"
        />
        {safe.body && (
          <RichText
            as="div"
            html={safe.body}
            className="text-lg opacity-80 max-w-2xl mx-auto"
            data-prop="body"
          />
        )}
      </div>

      {formState === "success" ? (
        <div className="text-center py-12" role="status" aria-live="polite">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
          <p className="text-lg font-medium">{safe.successMessage}</p>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={isTwoColumn ? "grid grid-cols-1 @md:grid-cols-2 gap-x-6 gap-y-5" : "space-y-5"}
        >
          {/* Honeypot -- visually hidden, traps bots */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
            <label htmlFor="__hp_website">Website</label>
            <input
              type="text"
              id="__hp_website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          {/* Name */}
          <div>
            <label htmlFor="lf-name" className={labelClass}>
              Name <span className="text-primary">*</span>
            </label>
            <input
              id="lf-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="Your full name"
              disabled={isEditor}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="lf-email" className={labelClass}>
              Email <span className="text-primary">*</span>
            </label>
            <input
              id="lf-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@example.com"
              disabled={isEditor}
            />
          </div>

          {/* Phone (optional, toggled by showPhone) */}
          {safe.showPhone && (
            <div>
              <label htmlFor="lf-phone" className={labelClass}>
                Phone
              </label>
              <input
                id="lf-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
                placeholder="(555) 123-4567"
                disabled={isEditor}
              />
            </div>
          )}

          {/* Custom fields */}
          {safe.fields.map((field, i) => {
            const fieldId = `lf-custom-${i}`;
            const val = customValues[i] || "";
            const updateVal = (v: string) =>
              setCustomValues((prev) => ({ ...prev, [i]: v }));

            return (
              <div key={i} className={field.type === "textarea" && isTwoColumn ? "@md:col-span-2" : ""}>
                <label htmlFor={fieldId} className={labelClass}>
                  {field.label}
                  {field.required && <span className="text-primary"> *</span>}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    id={fieldId}
                    required={field.required}
                    value={val}
                    onChange={(e) => updateVal(e.target.value)}
                    className={`${inputClass} min-h-[100px] resize-y`}
                    disabled={isEditor}
                  />
                ) : field.type === "select" ? (
                  <select
                    id={fieldId}
                    required={field.required}
                    value={val}
                    onChange={(e) => updateVal(e.target.value)}
                    className={inputClass}
                    disabled={isEditor}
                  >
                    <option value="">Select...</option>
                    {field.options
                      .split(",")
                      .map((opt) => opt.trim())
                      .filter(Boolean)
                      .map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                  </select>
                ) : (
                  <input
                    id={fieldId}
                    type="text"
                    required={field.required}
                    value={val}
                    onChange={(e) => updateVal(e.target.value)}
                    className={inputClass}
                    disabled={isEditor}
                  />
                )}
              </div>
            );
          })}

          {/* Message (optional, toggled by showMessage) */}
          {safe.showMessage && (
            <div className={isTwoColumn ? "@md:col-span-2" : ""}>
              <label htmlFor="lf-message" className={labelClass}>
                {safe.messageLabel}
              </label>
              <textarea
                id="lf-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className={`${inputClass} min-h-[120px] resize-y`}
                placeholder="Tell us about your situation..."
                disabled={isEditor}
              />
            </div>
          )}

          {/* Submit button */}
          <div className={isTwoColumn ? "@md:col-span-2" : ""}>
            <button
              type="submit"
              disabled={isEditor || formState === "submitting"}
              className="w-full @md:w-auto px-8 py-3 bg-primary text-primary-fg font-semibold rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {formState === "submitting" ? "Sending..." : safe.submitLabel}
            </button>
            {isEditor && (
              <p className="text-xs opacity-50 mt-2">Form submission is disabled in the editor.</p>
            )}
            {formState === "error" && (
              <p className="text-sm text-red-600 mt-2" role="alert">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        </form>
      )}
    </SectionShell>
  );
}
